<script lang="ts" setup>
import type { BlockSerializedChild } from '#layers/block-engine';
import type { ResolvedMonospaceCollection } from '#layers/data-model/types/schema/resolved-schema';
import { computed, ref, toRaw } from 'vue';
import { useI18n } from 'vue-i18n';
import { isBlockSerialized, isBlockSerializedChild, processAddBlock, useBlockAdapter, useBlockContext, useBlockResolverStore } from '#layers/block-engine';
import { TeleportPageHeaderActions } from '#layers/chrome';
import { isFieldHidden } from '#layers/data-model/utils/fields/fields';
import { getCollectionFields } from '#layers/data-model/utils/schema/collection';
import { usePageActions } from '#layers/spaces';
import { useDetailPageContext } from '~/composables/block-context/use-detail-page-context';
import { useContentPageCustomization } from '~/composables/use-content-page-customization';
// TODO permission check to only show this when the user can actually edit the form
import { useToastResultHandling } from '~/composables/use-toast-result-handling';
import { getFieldLabel, sortFieldsBySortKey } from '~/utils/fields/sort-fields';
import { isGeneratedKey } from '~/utils/generated';

const props = defineProps<{
	collection: ResolvedMonospaceCollection;
}>();

const { t } = useI18n();
const { saveBlockSerialized } = useBlockAdapter();
const { triggerBlockRefresh } = useBlockResolverStore();
const { editPage } = usePageActions();
const { customizeContentDetailPage } = useContentPageCustomization();
const { onError } = useToastResultHandling({
	error: {
		title: t('common.save_failed'),
	},
});

const { getChild, getRootBlock } = useBlockContext();
const detailPageContext = useDetailPageContext();
const addingFieldId = ref<string | null>(null);

const renderedFieldIds = computed(() => {
	const child = getChild();
	const rootBlock = getRootBlock();

	if (!isBlockSerialized(rootBlock)) {
		return new Set<string>();
	}

	return new Set(
		(child.slots.default ?? [])
			.map((childId) => rootBlock.children?.[childId])
			.filter((slotChild): slotChild is BlockSerializedChild =>
				isBlockSerializedChild(slotChild) && slotChild.blockId === '$builtin:FormField' && !!slotChild.props?.fieldId,
			)
			.map((slotChild) => slotChild.props.fieldId as string),
	);
});

const missingFields = computed(() =>
	sortFieldsBySortKey(
		getCollectionFields(props.collection, {
			includeConstrainedFields: false,
		}).filter((field) => !isFieldHidden(field) && !renderedFieldIds.value.has(field.id)),
	),
);

const hasMissingFields = computed(() => missingFields.value.length > 0);

const triggerLabel = computed(() =>
	t('blocks.form.missing_fields.count', { count: missingFields.value.length }),
);

async function addMissingField(fieldId: string) {
	if (addingFieldId.value) {
		return;
	}

	const rootBlock = getRootBlock();
	const child = getChild();

	if (!isBlockSerialized(rootBlock)) {
		return;
	}

	addingFieldId.value = fieldId;

	try {
		const updatedBlock = structuredClone(toRaw(rootBlock));
		const { data } = processAddBlock({
			editedBlock: updatedBlock,
			parentChildId: child.id,
			slot: 'default',
			blockId: '$builtin:FormField',
			props: {
				fieldId,
			},
		});

		Object.assign(updatedBlock, data);

		await saveBlockSerialized(updatedBlock);
		triggerBlockRefresh(updatedBlock.id);
	}
	catch (error) {
		onError(error);
	}
	finally {
		addingFieldId.value = null;
	}
}

async function editForm() {
	if (!detailPageContext.pageId || isGeneratedKey(detailPageContext.pageId)) {
		await customizeContentDetailPage(props.collection.id);
		return;
	}

	editPage(detailPageContext.pageId, {
		saveCurrentPath: true,
	});
}
</script>

<template>
	<TeleportPageHeaderActions v-if="hasMissingFields">
		<DPopover
			class="ml-auto"
			:content="{ side: 'bottom', align: 'end' }"
			:ui="{ content: 'w-80 p-3' }"
		>
			<DButton
				data-test-id="missing-fields-trigger"
				icon="lucide:triangle-alert"
				color="warning"
				variant="subtle"
			>
				{{ triggerLabel }}
			</DButton>

			<template #content>
				<div class="flex flex-col gap-3">
					<p class="text-xs text-muted">
						{{ $t('blocks.form.missing_fields.description') }}
					</p>

					<ul class="flex flex-col gap-1">
						<li
							v-for="field in missingFields"
							:key="field.id"
							data-test-id="missing-field-item"
							class="flex items-center justify-between gap-2 rounded-md border px-2.5 py-1.5"
						>
							<span class="truncate text-sm">
								{{ getFieldLabel(field) }}
							</span>

							<DButton
								:data-test-id="`missing-field-add-${field.id}`"
								variant="ghost"
								size="xs"
								:label="$t('blocks.form.missing_fields.add_to_form')"
								class="shrink-0"
								:loading="addingFieldId === field.id"
								:disabled="addingFieldId !== null"
								@click="addMissingField(field.id)"
							/>
						</li>
					</ul>

					<DButton
						data-test-id="edit-form-button"
						variant="outline"
						icon="lucide:pencil"
						:label="$t('blocks.form.missing_fields.edit_form')"
						block
						@click="editForm"
					/>
				</div>
			</template>
		</DPopover>
	</TeleportPageHeaderActions>
</template>
